import { useLocalization } from "@fluent/react";
import cc from "classcat";
import {
  Button,
  Chip,
  ResponsiveFlex,
  IconButton,
  Row,
} from "@narmi/design_system";
import { entities } from "byzantine";
import type { ChipProps } from "@narmi/design_system";
import type { InvoiceRecipientMatchDisplayState } from "byzantine/src/dbbl/businessLogic/entities/invoices/helpers";

import styles from "./RecipientCard.module.scss";

type DetailRow = {
  label: string;
  value: string;
  shouldWrapOnMobile?: boolean;
};

export type RecipientCardFields = {
  // Optional: the invoice match/extraction doesn't include an FI name, but a
  // fully-selected recipient (ACH flow) does.
  financialInstitution?: string;
  accountNumber: string;
  routingNumber: string;
};

type RecipientCardProps = {
  state?: InvoiceRecipientMatchDisplayState;
  title: string | null;
  fields: RecipientCardFields | null;
  onEdit?: () => void;
  onClear: () => void;
};

// "Matched" is the one badge that renders label-only - no leading icon
const ICONLESS_STATES = new Set<InvoiceRecipientMatchDisplayState>([
  "matchedBankDetails",
]);

const RecipientCard = ({
  state,
  title,
  fields,
  onEdit,
  onClear,
}: RecipientCardProps) => {
  const { l10n } = useLocalization();

  const descriptor = state
    ? entities.invoices.getInvoiceRecipientBadgeDescriptor(state)
    : null;
  const isWarning = descriptor?.variant === "warning";
  let startIcon: ChipProps["startIcon"];
  if (state && !ICONLESS_STATES.has(state)) {
    startIcon = isWarning ? "alert-circle" : "check";
  }

  const rows: DetailRow[] = fields
    ? [
        ...(fields.financialInstitution
          ? [
              {
                label: l10n.getString("recipient-card-financial-institution"),
                value: fields.financialInstitution,
                shouldWrapOnMobile: true,
              },
            ]
          : []),
        {
          label: l10n.getString("label-account-number"),
          value: fields.accountNumber,
        },
        {
          label: l10n.getString("label-routing"),
          value: fields.routingNumber,
        },
      ]
    : [];

  return (
    <div className={styles.recipientCardContainer}>
      <div
        className={cc([
          styles.recipientNameContainer,
          { [styles.recipientNameContainerWarning]: isWarning },
        ])}
      >
        <Row alignItems="top" justifyContent="space-between">
          <Row.Item>
            <div className="fontSize--m fontWeight--semibold">
              {title || "-"}
            </div>
            {descriptor && (
              <div className={styles.badge}>
                <Chip
                  kind={isWarning ? "warn" : "success"}
                  startIcon={startIcon}
                  label={l10n.getString(descriptor.l10nId)}
                />
              </div>
            )}
          </Row.Item>
          <Row.Item shrink>
            <div className={styles.recipientCardActionsContainer}>
              {onEdit && (
                <>
                  <Button
                    kind="plain"
                    onClick={onEdit}
                    label={l10n.getString("button-edit")}
                  />
                  <span className={styles.lineDivider} />
                </>
              )}
              <IconButton
                name="x"
                kind="plain"
                onClick={onClear}
                label={l10n.getString("wire-payments-table-clear-recipient")}
              />
            </div>
          </Row.Item>
        </Row>
      </div>
      {rows.length > 0 && (
        <div className={styles.recipientCardDetailsContainer}>
          <ResponsiveFlex gapSize="m">
            <div className="fontSize--xs fontWeight--bold">
              {l10n.getString("label-details").toUpperCase()}
            </div>
            {rows.map(({ label, value, shouldWrapOnMobile }) => (
              <div key={label}>
                <Row alignItems="center" justifyContent="space-between">
                  <Row.Item>
                    <span className="fontSize--m fontWeight--semibold">
                      {label}
                    </span>
                  </Row.Item>
                  <Row.Item
                    shrink={!shouldWrapOnMobile}
                    className={cc({
                      [styles.recipientCardDetailValueItemWrapOnMobile]:
                        shouldWrapOnMobile,
                    })}
                  >
                    <span
                      className={cc([
                        "fontSize--m fontWeight--semibold",
                        {
                          [styles.recipientCardDetailValueWrapOnMobile]:
                            shouldWrapOnMobile,
                        },
                      ])}
                    >
                      {value}
                    </span>
                  </Row.Item>
                </Row>
              </div>
            ))}
          </ResponsiveFlex>
        </div>
      )}
    </div>
  );
};

export default RecipientCard;
